import { useMemo, useState } from "react";

import { Box, Flex, Image, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { generatePath, useNavigate } from "react-router";

import { PageName } from "analytics/events";
import { routes } from "config/routes";
import { TaskCheckHeader } from "flows/Inventory/shared/components/TaskCheckHeader";
import { useGetProductStockQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { Page } from "shared/components/Page";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { transformChunks } from "shared/components/TextWrapper";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { BodyS, HeaderM, HeaderS, TitleS, TitleXS } from "ui/Typography/Typography";
import { getDisplayedDate } from "utils/formats";
import { formatImage } from "utils/image";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { CheckBackstockModal } from "../../components/CheckBackstockModal";
import { OutboundMoreThanExpectedStockModal } from "../../components/OutboundMoreThanExpectedStockModal";
import { ShelfIsEmptyModal } from "../../components/ShelfIsEmptyModal";
import { SkipStepButton } from "../../components/SkipStepButton";
import { useBbdCheckMutation } from "../../hooks/useBbdCheckMutation";
import { useBbdCheckService } from "../../hooks/useBbdCheckService";
import { useBbdCheckStore } from "../../stores/useBbdCheckStore";

const IMAGE_SIZE = 187;

export function BbdCheckDeclareExpired() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastAndPlayError } = useErrorToastWithAudio({ errorToastId: "stock_machine_error" });
  const { loading: serviceLoading, isInvalidParams } = useBbdCheckService();
  const store = useBbdCheckStore();
  const { performCheckUpdate, isUpdating } = useBbdCheckMutation();

  const {
    origin,
    shelfLetter,
    shelfNumber,
    checkId,
    sku,
    productImageUrl,
    productName,
    productCountryOfOriginName,
    minDaysToBestBeforeDate,
    productStock,
    setProductStock,
    declareQuantities,
    setCreateCheckType,
    resetCreateCheckType,
  } = store;

  const { loading: stockLoading } = useGetProductStockQuery({
    fetchPolicy: "network-only",
    variables: { sku: sku!, isAlwaysInbound: true },
    skip: !sku || serviceLoading,
    onCompleted: (stockData) => {
      setProductStock(stockData?.getProduct.inventoryEntry.stock?.shelf ?? 0);
    },
    onError: () => {
      showToastAndPlayError();
      navigate(-1);
    },
  });

  const [tooGoodToGoQuantity, setTooGoodToGoQuantity] = useState<number>(0);
  const [expiredQuantity, setExpiredQuantity] = useState<number>(0);

  const {
    isOpen: isShelfIsEmptyModalOpen,
    onOpen: onOpenShelfIsEmptyModal,
    onClose: onCloseShelfIsEmptyModal,
  } = useDisclosure();

  const {
    isOpen: isCheckBackstockModalOpen,
    onOpen: onOpenCheckBackstockModal,
    onClose: onCloseCheckBackstockModal,
  } = useDisclosure();

  const {
    isOpen: isOutboundMoreThanExpectedStockModalOpen,
    onOpen: onOpenOutboundMoreThanExpectedStockModal,
    onClose: onCloseOutboundMoreThanExpectedStockModal,
  } = useDisclosure();

  const goToDeclareClosestBbd = () => {
    declareQuantities({
      expiredQuantity,
      tooGoodToGoQuantity,
    });
    resetCreateCheckType();
    navigate(
      generatePath(routes.inventory.bbdChecks.declareClosestBdd, {
        checkId: checkId!,
      }),
    );
  };

  const skipDeclareClosestBbd = () => {
    declareQuantities({
      expiredQuantity,
      tooGoodToGoQuantity,
    });
    performCheckUpdate();
  };

  const handleConfirm = () => {
    const totalOutboundedQuantity = tooGoodToGoQuantity + expiredQuantity;

    const newProductStock = productStock - totalOutboundedQuantity;

    if (newProductStock < 0) {
      sendSegmentTrackEvent("inAppMessageShown", {
        screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
        component_name: "more_items_to_outbound_than_expected",
        component_content: checkId!,
      });
      onOpenOutboundMoreThanExpectedStockModal();
      return;
    }

    if (newProductStock === 0) {
      sendSegmentTrackEvent("inAppMessageShown", {
        screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
        component_name: "the_shelf_is_empty",
        component_content: checkId!,
      });
      onOpenShelfIsEmptyModal();
      return;
    }

    goToDeclareClosestBbd();
  };

  const handleConfirmOutboundMoreThanExpectedStock = () => {
    sendSegmentTrackEvent("click", {
      screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
      component_name: "more_items_to_outbound_than_expected",
      component_value: "Yes",
      component_content: checkId!,
    });
    onCloseOutboundMoreThanExpectedStockModal();
    onOpenShelfIsEmptyModal();
  };

  const handleConfirmShelfIsEmpty = () => {
    skipDeclareClosestBbd();
    onCloseShelfIsEmptyModal();
  };

  const handleEnterDateClick = () => {
    sendSegmentTrackEvent("click", {
      screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
      component_name: "the_shelf_is_empty",
      component_value: "enter_a_date",
      component_content: checkId!,
    });
    setCreateCheckType("QUANTITY_AVAILABLE");
    goToDeclareClosestBbd();
  };

  const onClickOutOfStock = () => {
    sendSegmentTrackEvent("click", {
      screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
      component_name: "please_check_the_backstock",
      component_value: "out_of_stock",
      component_content: checkId!,
    });
    setCreateCheckType("NO_QUANTITY");
    declareQuantities({
      expiredQuantity: 0,
      tooGoodToGoQuantity: 0,
    });
    performCheckUpdate();
    onCloseCheckBackstockModal();
  };

  const onClickSkipStepButton = () => {
    sendSegmentTrackEvent("inAppMessageShown", {
      screen_name: PageName.INVENTORY_BBD_DECLARE_EXPIRED,
      component_name: "please_check_the_backstock",
      component_content: checkId!,
    });
    onOpenCheckBackstockModal();
  };

  const imageUrlFormatted = isNotNullNorUndefined(productImageUrl)
    ? formatImage(productImageUrl, IMAGE_SIZE, IMAGE_SIZE)
    : null;

  const computedExpiryDate = useMemo(() => {
    const date = new Date();
    const minDaysToBBD = minDaysToBestBeforeDate ?? 0;
    const numberOfDaysToAdd = Math.max(-1, minDaysToBBD - 1);
    date.setDate(date.getDate() + numberOfDaysToAdd);
    return date;
  }, [minDaysToBestBeforeDate]);

  if (serviceLoading || isInvalidParams) {
    return null;
  }

  return (
    <>
      <Page isLoading={stockLoading} isCentered isBgGrey isFull>
        <TaskCheckHeader
          headerMessageId="flows.inventory.checks.bbd-checks.pages.bbd-check-declare-expired.bbd-check"
          origin={origin}
          shelfLetter={shelfLetter}
          shelfNumber={shelfNumber}
        />
        <Flex direction="column" flex={1} w="100%" alignItems="center" overflowY="scroll">
          <Flex direction="column" alignItems="center" gap="s100">
            <Flex
              direction="column"
              justifyContent="center"
              alignItems="center"
              bg="purple.100"
              paddingX="s200"
              paddingY="s150"
              w="100%"
            >
              <BodyS>
                {intl.formatMessage({
                  id: "flows.inventory.checks.bbd-checks.pages.bbd-check-declare-expired.banner.label",
                })}
              </BodyS>
              <HeaderS data-testid="expiry-date">{getDisplayedDate(computedExpiryDate)}</HeaderS>
            </Flex>
            {isNotNullNorUndefined(imageUrlFormatted) && (
              <Image
                src={imageUrlFormatted}
                objectFit="contain"
                h="60vh"
                maxW={`${IMAGE_SIZE}px`}
                maxH={`${IMAGE_SIZE}px`}
              />
            )}
            <Flex px="s200" gap="s100">
              <Flex
                justifyContent="center"
                alignItems="center"
                px="s200"
                py="s100"
                bg="white"
                borderRadius="sm"
                alignSelf="stretch"
                w="100%"
              >
                <BodyS textAlign="center" color="grey.800" data-testid="stock-check-product-name">
                  {productName}
                  {productCountryOfOriginName ? ` (${productCountryOfOriginName})` : null}
                </BodyS>
              </Flex>
              <Flex
                direction="column"
                justifyContent="center"
                alignItems="center"
                p="s100"
                bg="white"
                borderRadius="sm"
              >
                <TitleS color="grey.800">
                  {intl.formatMessage({
                    id: "flows.inventory.checks.bbd-checks.pages.bbd-check-declare-expired.stock-label",
                  })}
                </TitleS>
                <HeaderM data-testid="product-stock">{productStock}</HeaderM>
              </Flex>
            </Flex>
            <Box
              bg="purple.100"
              px="s100"
              py="s50"
              mt="s100"
              ml="s200"
              borderRadius="4px"
              alignSelf="start"
            >
              <TitleXS data-testid="todays-date">
                <FormattedMessage id="flows.inventory.checks.bbd-checks.pages.bbd-check-declare-expired.todays-date" />
                {` ${getDisplayedDate(new Date())}`}
              </TitleXS>
            </Box>
            <Box w="100%" bg="white" px="s300" py="s200">
              <IncreaseDecrease
                mb="s200"
                label={
                  <FormattedMessage
                    id="components.increasedecrease.too_good_to_go"
                    values={transformChunks({ b: { as: "strong" } })}
                  />
                }
                changeHandler={setTooGoodToGoQuantity}
                count={tooGoodToGoQuantity}
                dataTestId="tgtg-quantity"
                withInput
              />
              <IncreaseDecrease
                label={
                  <FormattedMessage
                    id="components.increasedecrease.how_many_are_expired"
                    values={transformChunks({ b: { as: "strong" } })}
                  />
                }
                count={expiredQuantity}
                changeHandler={setExpiredQuantity}
                dataTestId="expired-quantity"
                withInput
              />
            </Box>
          </Flex>
          <SkipStepButton
            onClick={onClickSkipStepButton}
            label={intl.formatMessage({
              id: "flows.inventory.bbd-checks.pages.declare-expired.shelf-is-empty-button-label",
            })}
          />
        </Flex>
        <Box
          bg="white"
          borderTopWidth="1px"
          borderTopStyle="solid"
          borderTopColor="grey.200"
          p="s200"
          width="100%"
        >
          <Button
            disabled={isUpdating}
            onClick={handleConfirm}
            isFullWidth
            variant="primary"
            size="lg"
            data-testid="submit-quantities-button"
          >
            <FormattedMessage id="pages.task.taskIndex.confirm_amount" />
          </Button>
        </Box>
      </Page>
      <ShelfIsEmptyModal
        isOpen={isShelfIsEmptyModalOpen}
        onClickConfirm={handleConfirmShelfIsEmpty}
        onClickEnterDate={handleEnterDateClick}
      />
      <CheckBackstockModal
        isOpen={isCheckBackstockModalOpen}
        onClickConfirm={onCloseCheckBackstockModal}
        onClickOutOfStock={onClickOutOfStock}
        totalUnits={productStock}
      />
      <OutboundMoreThanExpectedStockModal
        isOpen={isOutboundMoreThanExpectedStockModalOpen}
        onClickConfirm={handleConfirmOutboundMoreThanExpectedStock}
        onClickNo={onCloseOutboundMoreThanExpectedStockModal}
        totalUnits={tooGoodToGoQuantity + expiredQuantity}
      />
      <SpinnerModal isOpen={isUpdating} />
    </>
  );
}
